import React, {
  CSSProperties,
  useEffect,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import {getEndpoint, getHeaders, getMPPEndpoint} from '../../../helpers/endpoint';
import { ArticleEditor } from '../Editor';
import { ChangelogArticle, ChangelogVersion, FormattedChangelog } from '@lfp-digital/back-types-mpg';
import { map, omit } from 'lodash';

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    margin: 16,
  },
  titleContainer: {
    display: 'flex',
    height: 43,
    backgroundColor: 'white',
    width: '100%',
    alignItems: 'center',
    fontSize: 18,
    paddingLeft: 16,
  },
  saveButton: {
    height: 48,
    width: 150,
    color: '#4054CC',
    borderRadius: 4,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#4054CC',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  disabledButton: {
    height: 48,
    width: 150,
    color: 'white',
    borderRadius: 4,
    backgroundColor: '#959DAF',
    fontWeight: 'bold',
    borderWidth: 0,
  },
};

const createArticle = (
  isMPP: boolean,
  version: string | undefined,
  payload: Omit<ChangelogArticle, 'uuid'>,
  onSuccess: (response: ChangelogVersion) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('article', {version}), {
      method: 'POST',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const updateArticle = (
  isMPP: boolean,
  version: string | undefined,
  payload: any,
  onSuccess: (response: any) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('article', {version}), {
      method: 'PATCH',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const getVersion = (
  isMPP: boolean,
  version: string,
  onSuccess: (response: ChangelogVersion) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('version', version), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const getChangelog = (
  isMPP: boolean,
  onSuccess: (response: FormattedChangelog) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('getChangelog'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const deleteArticle = (
  isMPP: boolean,
  version: string | undefined,
  payload: any,
  onSuccess: (response: any) => void,
) => {
  return (_dispatch, getState) => {
    return fetch((isMPP ? getMPPEndpoint : getEndpoint)('article', {version, uuid: payload.uuid}), {
      method: 'DELETE',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

export const Article = ({history, match}) => {
  const dispatch = useDispatch();

  const [version, setVersion] = useState<string>();
  const [versions, setVersions] = useState<string[]>();
  const [article, setArticle] = useState<Partial<ChangelogArticle>>({tags: []});

  const versionId = match.params?.version;
  const articleId = match.params?.article;
  const isMPP = match?.path?.includes('mpp');

  const updatedVersion = versionId && versionId !== version;

  const onChange = (key: string, value: string | number) => {
    setArticle({...article, [key]: value});
  };

  const onSuccess = (response: ChangelogVersion) => {
    const data = response?.articles?.find(article => article.name === articleId);
    if (data) {
      setArticle(data);
    }
  };

  useEffect(() => {
    if (versionId) {
      dispatch(getVersion(isMPP, versionId, onSuccess));
      setVersion(versionId);
    } else {
      setArticle({tags: []});
    }
  }, [versionId]);

  useEffect(() => {
    dispatch(
      getChangelog(isMPP, (response) =>
        setVersions(map(response.versions, 'version')),
      ),
    );
  }, []);

  const onSubmit = () => {
    const onSuccess = () => {
      history.push(isMPP ? '/mpp/article/list' : '/article/list');
    };
    const onDeleteSucces = () => {
      // @ts-ignore
      dispatch(createArticle(isMPP, version, omit(article, 'uuid'), onSuccess));
    }
    if (articleId && !updatedVersion) {
      dispatch(updateArticle(isMPP, version, article, onSuccess));
    } else if (updatedVersion) {
      dispatch(deleteArticle(isMPP, versionId, article, onDeleteSucces));
    } else {
      // @ts-ignore
      dispatch(createArticle(isMPP, version, article, onSuccess));
    }
  };

  const isButtonDisabled = !version || !article?.name || !article?.title || !article?.text || !article?.tags;

  return (
    <div style={styles.container}>
      <div style={styles.titleContainer}>{articleId ? 'Update' : 'Create'} article</div>
      <ArticleEditor
        onChange={onChange}
        data={article}
        versions={versions}
        version={version}
        setVersion={setVersion}
        isCreation={!articleId}
        isMPP={isMPP}
      />
      <div style={styles.buttonContainer}>
        <button
          disabled={isButtonDisabled}
          style={!isButtonDisabled ? styles.saveButton : styles.disabledButton}
          onClick={onSubmit}>
          Sauvegarder
        </button>
        <span style={{marginLeft: 8, color: 'red'}}>*</span>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/Articles/Article/index.tsx